import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Replayer } from 'rrweb';
import 'rrweb/dist/rrweb.min.css';
import { clock, flag, formatStay, fullDate, place, sectionName, VIDEO_SHORT, when } from './format';

// Le snapshot est enregistré sans CSS inline (ReplayRecorder : inlineStylesheet false) : on les recharge depuis le site.
const SITE = 'https://gautbonac.com/';

const FILTERS = [
  { id: 'all', label: 'Tous', test: () => true },
  { id: 'live', label: 'En direct', test: (person) => person.live },
  { id: 'image', label: 'Avec image', test: (person) => person.ready },
  { id: 'join', label: 'Ont cliqué rejoindre', test: (person) => person.joined },
  { id: 'paid', label: 'Ont payé', test: (person) => person.paid },
];

const SORTS = [
  { id: 'best', label: 'Du mieux noté' },
  { id: 'worst', label: 'Moins bien noté' },
];

function compareMotion(order) {
  const direction = order === 'best' ? -1 : 1;
  return (a, b) => {
    const left = typeof a.motion === 'number' ? a.motion : null;
    const right = typeof b.motion === 'number' ? b.motion : null;
    if (left === null || right === null) {
      if (left === right) return (b.arrivedAt || 0) - (a.arrivedAt || 0);
      return left === null ? 1 : -1;
    }
    if (left !== right) return (left - right) * direction;
    return (b.arrivedAt || 0) - (a.arrivedAt || 0);
  };
}

function motionLabel(score) {
  return typeof score === 'number' ? `${score}/10` : '—';
}

function motionColor(score) {
  if (typeof score !== 'number') return '#a8a29e';
  const t = (score - 1) / 9;
  const green = Math.round(80 + t * 140);
  return `rgb(${Math.round(160 - t * 120)}, ${green}, ${Math.round(90 - t * 40)})`;
}

function stayOf(person, now) {
  if (person.live && person.arrivedAt) return now - person.arrivedAt;
  return person.stayMs;
}

function Steps({ person }) {
  return (
    <span className="ck-chips">
      {person.joined && <i className="ck-chip">Clic</i>}
      {person.checkout && <i className="ck-chip">Checkout</i>}
      {person.paid && <i className="ck-chip is-paid">Payé</i>}
      {person.ready && <i className="ck-chip is-image" title="Replay disponible">▶ image</i>}
    </span>
  );
}

function Row({ person, now, active, onOpen }) {
  return (
    <li>
      <button type="button" className={`ck-row${active ? ' is-on' : ''}`} onClick={() => onOpen(person.sid)}>
        <span className={`ck-dot ${person.live ? 'is-live' : 'is-gone'}`} title={person.live ? 'Encore sur le site' : 'Parti'} />
        <span className="ck-row-main">
          <b>
            {flag(person.country)} {place(person)}
            {person.owner && <em className="ck-tag">toi</em>}
            {person.horsStats && !person.owner && <em className="ck-tag">Interne · hors stats</em>}
          </b>
          <small>
            {when(person.arrivedAt)} → {person.live ? 'encore là' : when(person.leftAt)}
            {' · '}{person.version}
            {person.source ? ` · ${person.source}` : ''}
          </small>
        </span>
        <span className="ck-row-side">
          <b className="ck-motion" style={{ color: motionColor(person.motion) }} title="1 = écran figé, 10 = beaucoup de déplacements">{motionLabel(person.motion)}</b>
          <b className="ck-green">{formatStay(stayOf(person, now))}</b>
          <Steps person={person} />
        </span>
      </button>
    </li>
  );
}

/** Lecteur rrweb à l'échelle de la colonne : lecture, pause, retour au début, piste cliquable, vitesse. */
function ReplayPlayer({ events, onReady }) {
  const boxRef = useRef(null);
  const stageRef = useRef(null);
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [error, setError] = useState('');

  useEffect(() => {
    const stage = stageRef.current;
    const box = boxRef.current;
    if (!stage || !box) return undefined;
    stage.replaceChildren();
    setError('');
    let replayer;
    try {
      replayer = new Replayer(events, { root: stage, mouseTail: false, showWarning: false, skipInactive: true, triggerFocus: false });
    } catch (err) {
      setError(err?.message || 'Lecture impossible.');
      return undefined;
    }
    playerRef.current = replayer;
    const meta = replayer.getMetaData();
    setTotal(meta.totalTime || 0);
    const dress = () => {
      const doc = replayer.iframe?.contentDocument;
      if (!doc?.head) return;
      if (!doc.querySelector('base')) {
        const base = doc.createElement('base');
        base.href = SITE;
        doc.head.prepend(base);
      }
      // Le snapshot pointe vers le CSS du moment de la visite. Après un déploiement ce fichier n'existe plus :
      // le replay s'affiche sans mise en page. On enlève ces liens et on met le CSS actuellement servi.
      const live = [...document.querySelectorAll('link[rel="stylesheet"]')]
        .map((link) => link.href)
        .find((href) => /\/static\/css\/main\.[^/?#]+\.css/.test(href));
      if (!live) return;
      doc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
        const href = link.getAttribute('href') || '';
        if (/main\.[^/]+\.css/.test(href)) link.remove();
      });
      if (!doc.querySelector('link[data-live-css]')) {
        const link = doc.createElement('link');
        link.rel = 'stylesheet';
        link.href = live;
        link.setAttribute('data-live-css', '1');
        doc.head.appendChild(link);
      }
    };
    replayer.on('fullsnapshot-rebuilded', dress);
    replayer.on('finish', () => setPlaying(false));
    let size = '';
    const fit = () => {
      const iframe = stage.querySelector('iframe');
      const wrapper = stage.querySelector('.replayer-wrapper');
      if (!iframe || !wrapper) return;
      const w = parseFloat(iframe.getAttribute('width')) || iframe.offsetWidth || 1;
      const h = parseFloat(iframe.getAttribute('height')) || iframe.offsetHeight || 1;
      const maxH = Math.min(window.innerHeight * 0.66, 780);
      const scale = Math.min(1, box.clientWidth / w, maxH / h);
      const next = `${w}x${h}@${scale.toFixed(4)}`;
      if (next === size) return;
      size = next;
      wrapper.style.transform = `scale(${scale})`;
      wrapper.style.transformOrigin = 'top left';
      stage.style.width = `${Math.round(w * scale)}px`;
      stage.style.height = `${Math.round(h * scale)}px`;
    };
    setPlaying(false);
    replayer.pause(0);
    const dressTimers = [150, 600, 1500].map((delay) => window.setTimeout(dress, delay));
    requestAnimationFrame(fit);
    const tick = window.setInterval(() => {
      setCurrent(replayer.getCurrentTime());
      fit();
    }, 250);
    const resize = new ResizeObserver(() => { size = ''; fit(); });
    resize.observe(box);
    onReady?.({
      startTime: meta.startTime,
      total: meta.totalTime,
      seek: (offset) => {
        replayer.play(Math.max(0, Math.min(meta.totalTime, offset)));
        setPlaying(true);
      },
    });
    return () => {
      dressTimers.forEach((timer) => window.clearTimeout(timer));
      window.clearInterval(tick);
      resize.disconnect();
      onReady?.(null);
      try { replayer.pause(); replayer.destroy?.(); } catch { /* déjà détruit */ }
      playerRef.current = null;
    };
  }, [events, onReady]);

  const toggle = () => {
    const replayer = playerRef.current;
    if (!replayer) return;
    if (playing) {
      replayer.pause();
      setPlaying(false);
    } else {
      replayer.play(current >= total - 50 ? 0 : current);
      setPlaying(true);
    }
  };
  const restart = () => {
    playerRef.current?.play(0);
    setPlaying(true);
  };
  const seekAt = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offset = ((event.clientX - rect.left) / rect.width) * total;
    if (playing) playerRef.current?.play(offset);
    else playerRef.current?.pause(offset);
    setCurrent(offset);
  };
  const nextSpeed = () => {
    const value = speed === 1 ? 2 : speed === 2 ? 4 : 1;
    playerRef.current?.setConfig({ speed: value });
    setSpeed(value);
  };

  return (
    <div className="ck-player">
      <div className="ck-player-box" ref={boxRef}>
        <div className="ck-player-stage" ref={stageRef} />
        {error && <p className="ck-player-msg">{error}</p>}
      </div>
      <div className="ck-player-bar">
        <button type="button" className="ck-btn is-primary ck-play" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? '❚❚ Pause' : '▶ Play'}
        </button>
        <button type="button" className="ck-btn" onClick={restart} aria-label="Rejouer depuis le début">↺</button>
        <div className="ck-player-track" onClick={seekAt} role="slider" aria-label="Position dans le replay" aria-valuemin={0} aria-valuemax={total} aria-valuenow={current} tabIndex={0}>
          <i style={{ width: total ? `${Math.min(100, (current / total) * 100)}%` : 0 }} />
        </div>
        <span className="ck-player-time">{clock(current)} / {clock(total)}</span>
        <button type="button" className="ck-btn" onClick={nextSpeed} title="Vitesse">×{speed}</button>
      </div>
    </div>
  );
}

function frise(person, detail) {
  const items = [];
  (detail?.activity || []).forEach((item) => {
    if (item.k === 'arrive') items.push({ at: item.at, kind: 'arrive', text: `Arrivée sur ${item.page || '/'}` });
    if (item.k === 'page') items.push({ at: item.at, kind: 'page', text: `Change de page : ${item.page}` });
    if (item.k === 'section') items.push({ at: item.at, kind: 'section', text: `Section ${sectionName(item.id)}` });
    if (item.k === 'click') {
      const where = item.section ? ` · ${sectionName(item.section)}` : '';
      items.push({ at: item.at, kind: 'click', text: item.label ? `Clic « ${item.label} »${where}` : `Clic dans la page${where}`, click: item });
    }
    if (item.k === 'video') {
      const name = VIDEO_SHORT[item.id] || item.id;
      items.push({ at: item.at, kind: 'video', text: item.mark === 0 ? `Lance ${name}` : `${name} : ${item.mark} %` });
    }
    if (item.k === 'leave') items.push({ at: item.at, kind: 'leave', text: 'Départ' });
  });
  const hasArrive = items.some((item) => item.kind === 'arrive');
  (detail?.steps || []).forEach((step) => {
    if (step.type === 'visit' && !hasArrive) items.push({ at: step.at, kind: 'arrive', text: `Arrivée sur ${step.page || '/'}` });
    if (step.type === 'join') items.push({ at: step.at, kind: 'step', text: `Clic rejoindre${step.button ? ` (${step.button})` : ''}` });
    if (step.type === 'checkout') items.push({ at: step.at, kind: 'step', text: 'Checkout Stripe ouvert' });
    if (step.type === 'paid') items.push({ at: step.at, kind: 'paid', text: 'Paiement confirmé' });
  });
  items.sort((a, b) => a.at - b.at);
  if (!person.live && !items.some((item) => item.kind === 'leave') && person.leftAt) {
    items.push({ at: person.leftAt, kind: 'last', text: 'Dernier signe de vie' });
  }
  return items;
}

function ClickMap({ clicks, sections, onPick }) {
  const bands = sections.filter((item) => typeof item.top === 'number').sort((a, b) => a.top - b.top);
  return (
    <div className="ck-map" aria-label="Plan de la page avec les clics numérotés">
      {bands.map((band) => (
        <span key={band.id} className="ck-map-band" style={{ top: `${band.top * 100}%` }}>{sectionName(band.id)}</span>
      ))}
      {clicks.map((click, index) => (
        click.y === null || click.y === undefined ? null : (
          <button
            key={`${click.at}-${index}`}
            type="button"
            className="ck-map-dot"
            style={{ left: `${(click.x ?? 0.5) * 100}%`, top: `${click.y * 100}%` }}
            title={click.label || 'Clic dans la page'}
            onClick={() => onPick(click.at)}
          >
            {index + 1}
          </button>
        )
      ))}
    </div>
  );
}

function Dossier({ person, token, now, activitySince, onBack }) {
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState('Chargement…');
  const [reload, setReload] = useState(0);
  const playerRef = useRef(null);
  const onReady = useCallback((player) => { playerRef.current = player; }, []);

  useEffect(() => {
    let stop = false;
    setStatus('Chargement…');
    fetch(`/api/analytics/replay?sid=${encodeURIComponent(person.sid)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.json())
      .then((body) => {
        if (stop) return;
        setDetail(body);
        setStatus('');
      })
      .catch(() => { if (!stop) setStatus('Le dossier ne répond pas.'); });
    return () => { stop = true; };
  }, [person.sid, token, reload]);

  const events = useMemo(() => {
    const list = detail?.events || [];
    return list.some((event) => event.type === 2) ? list : null;
  }, [detail]);
  const items = useMemo(() => frise(person, detail), [person, detail]);
  const clicks = (detail?.activity || []).filter((item) => item.k === 'click');
  const sections = (detail?.activity || []).filter((item) => item.k === 'section');
  const origin = person.arrivedAt || items[0]?.at || 0;
  const skew = Number(detail?.skew) || 0;
  const jump = (at) => {
    const player = playerRef.current;
    if (!player) return;
    // Heure serveur → heure du navigateur (horloge des événements rrweb) → position dans le replay.
    player.seek(at - skew - player.startTime);
  };
  const videos = Object.entries(person.videos || {}).filter(([, mark]) => mark >= 0);

  return (
    <article className="ck-dossier">
      <header className="ck-dossier-head">
        {onBack && <button type="button" className="ck-btn ck-back" onClick={onBack}>← Liste</button>}
        <div>
          <p className="ck-kicker">
            <span className={`ck-dot ${person.live ? 'is-live' : 'is-gone'}`} /> {person.live ? 'Sur le site en ce moment' : 'Parti'}
            {person.owner && <em className="ck-tag">toi</em>}
            {person.horsStats && !person.owner && <em className="ck-tag">Interne · hors stats</em>}
          </p>
          <h2>{flag(person.country)} {place(person)}</h2>
          <p className="ck-sub">
            {[person.device, person.version, person.source || 'source directe ou inconnue'].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="ck-dossier-stay">
          <b className="ck-green">{formatStay(stayOf(person, now))}</b>
          <small>
            Arrivé {when(person.arrivedAt)}
            {' · '}
            {person.live ? 'encore là' : `parti ${when(person.leftAt)}`}
          </small>
          <Steps person={person} />
        </div>
      </header>

      <section className="ck-block">
        <div className="ck-block-head">
          <h3>Replay de son écran</h3>
          {person.live && <button type="button" className="ck-btn" onClick={() => setReload((n) => n + 1)}>Actualiser</button>}
        </div>
        {status && <p className="ck-empty">{status}</p>}
        {!status && events && <ReplayPlayer events={events} onReady={onReady} />}
        {!status && events && (person.motion || 1) <= 2 && (
          <p className="ck-empty">Note {person.motion || 1}/10 : presque aucun déplacement dans l’enregistrement. Le temps vert, c’est le téléphone resté ouvert, pas la durée du film.</p>
        )}
        {!status && !events && (
          <div className="ck-noimage">
            <b>Image non enregistrée</b>
            <p>Ce parcours n’a pas d’image complète de l’écran (événement rrweb de type 2). Il ne peut pas être relu. Les visites enregistrées depuis le correctif ont leur image, même quand elles durent quelques secondes.</p>
          </div>
        )}
      </section>

      <div className="ck-dossier-cols">
        <section className="ck-block">
          <h3>Frise</h3>
          {items.length === 0 && (
            <p className="ck-empty">
              Pas de frise pour cette visite.
              {activitySince && origin && origin < activitySince ? ` La frise détaillée existe pour les visites depuis le ${fullDate(activitySince)}.` : ''}
            </p>
          )}
          <ol className="ck-frise">
            {items.map((item, index) => (
              <li key={`${item.at}-${index}`} className={`is-${item.kind}`}>
                <span className="ck-frise-t">+{clock(item.at - origin)}</span>
                <span className="ck-frise-dot" />
                <span className="ck-frise-text">
                  {events ? <button type="button" onClick={() => jump(item.at)} title="Voir ce moment dans le replay">{item.text}</button> : item.text}
                  <small>{when(item.at)}</small>
                </span>
              </li>
            ))}
          </ol>
        </section>
        <section className="ck-block">
          <h3>Clics</h3>
          {clicks.length === 0 ? <p className="ck-empty">Aucun clic enregistré.</p> : (
            <div className="ck-clicks">
              <ClickMap clicks={clicks} sections={sections} onPick={jump} />
              <ol>
                {clicks.map((click, index) => (
                  <li key={`${click.at}-${index}`}>
                    <button type="button" onClick={() => jump(click.at)} disabled={!events}>
                      <i>{index + 1}</i>
                      <span className="ck-clicks-t">{clock(click.at - origin)}</span>
                      <span>{click.label || 'Clic dans la page'}<small>{sectionName(click.section)}</small></span>
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {videos.length > 0 && (
            <>
              <h3>Vidéos</h3>
              <ul className="ck-chips is-list">
                {videos.map(([id, mark]) => <li key={id} className="ck-chip">{VIDEO_SHORT[id] || id} · {mark === 0 ? 'lancée' : `${mark} %`}</li>)}
              </ul>
            </>
          )}
        </section>
      </div>
    </article>
  );
}

export default function People({ people, token, now, selected, onSelect, activitySince }) {
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('');
  const [limit, setLimit] = useState(120);
  const [narrow, setNarrow] = useState(() => window.innerWidth < 1100);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 1100);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const rule = FILTERS.find((item) => item.id === filter) || FILTERS[0];
  const matched = people.filter(rule.test);
  const list = sort ? [...matched].sort(compareMotion(sort)) : matched;
  const person = people.find((item) => item.sid === selected);

  useEffect(() => {
    if (!selected && !narrow && list[0]) onSelect(list[0].sid);
  }, [selected, narrow, list, onSelect]);

  const showList = !narrow || !person;
  return (
    <div className={`ck-people${narrow ? ' is-narrow' : ''}`}>
      {showList && (
        <aside className="ck-people-list">
          <div className="ck-filters">
            {FILTERS.map((item) => (
              <button key={item.id} type="button" className={filter === item.id ? 'is-on' : ''} onClick={() => setFilter(item.id)}>
                {item.label} <small>{people.filter(item.test).length}</small>
              </button>
            ))}
            {SORTS.map((item) => (
              <button key={item.id} type="button" className={sort === item.id ? 'is-on' : ''} title="Note de déplacement, de 1 à 10" onClick={() => setSort(sort === item.id ? '' : item.id)}>
                {item.label}
              </button>
            ))}
          </div>
          {list.length === 0 && <p className="ck-empty">Personne dans ce filtre.</p>}
          <ul>
            {list.slice(0, limit).map((item) => (
              <Row key={item.sid} person={item} now={now} active={item.sid === selected} onOpen={onSelect} />
            ))}
          </ul>
          {list.length > limit && <button type="button" className="ck-btn ck-more" onClick={() => setLimit(limit + 120)}>Voir plus ({list.length - limit})</button>}
        </aside>
      )}
      {person ? (
        <Dossier key={person.sid} person={person} token={token} now={now} activitySince={activitySince} onBack={narrow ? () => onSelect('') : null} />
      ) : (!narrow && <p className="ck-empty ck-dossier">Choisis une visite dans la liste.</p>)}
    </div>
  );
}
